import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Rating,
  TextField,
  useTheme,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Message as MessageSquareIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon
} from '@mui/icons-material';
import Button from './UIKit/Button';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getUserAvatarSrc } from '../utils/avatarUtils';
import { formatDistanceToNow } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { forumAPI } from '../services/api';

const TopicCard = ({
  topic,
  onLike,
  onAddComment,
}) => {
  const userRating = topic?.currentUserRating || 0;
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation(['forum', 'common']);
  const theme = useTheme();
  
  const [showAllComments, setShowAllComments] = useState({});
  const [showReplyForm, setShowReplyForm] = useState({});
  const [replyText, setReplyText] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [commentLikes, setCommentLikes] = useState({});
  const [commentDislikes, setCommentDislikes] = useState({});

  const getIdString = useCallback((value) => {
    if (!value) return '';
    return typeof value === 'string' ? value : value.toString();
  }, []);

  // Initialize liked state
  useEffect(() => {
    if (user?._id && Array.isArray(topic.likes)) {
      setIsLiked(topic.likes.some((likeId) => getIdString(likeId) === user._id));
    } else if (!user) {
      setIsLiked(false);
    }
  }, [user, topic.likes, getIdString]);

  useEffect(() => {
    if (user?._id && Array.isArray(topic.dislikes)) {
      setIsDisliked(topic.dislikes.some((dislikeId) => getIdString(dislikeId) === user._id));
    } else if (!user) {
      setIsDisliked(false);
    }
  }, [user, topic.dislikes, getIdString]);

  // Initialize comment likes state
  useEffect(() => {
    if (user?._id && Array.isArray(topic.comments)) {
      const likes = {};
      const dislikes = {};
      topic.comments.forEach((comment) => {
        if (Array.isArray(comment.likes)) {
          likes[comment._id] = comment.likes.some((likeId) => getIdString(likeId) === user._id);
        }
        if (Array.isArray(comment.dislikes)) {
          dislikes[comment._id] = comment.dislikes.some((dislikeId) => getIdString(dislikeId) === user._id);
        }
      });
      setCommentLikes(likes);
      setCommentDislikes(dislikes);
    } else if (!user) {
      setCommentLikes({});
      setCommentDislikes({});
    }
  }, [user, topic.comments, getIdString]);

  // Unified date formatting function
  const formatDate = useMemo(() => {
    return (dateString, format = 'relative') => {
      const date = new Date(dateString);
      const locale = i18n.language === 'ru' ? ru : enUS;
      const localeString = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
      
      return format === 'relative' 
        ? formatDistanceToNow(date, { addSuffix: true, locale })
        : date.toLocaleDateString(localeString, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
    };
  }, [i18n.language]);

  // Sort and limit comments for display
  const getDisplayComments = useCallback((comments, topicId) => {
    if (!Array.isArray(comments)) return [];
    
    // Sort comments by creation date (oldest first)
    const sortedComments = [...comments].sort((a, b) => 
      new Date(a.createdAt) - new Date(b.createdAt)
    );
    
    // If showing all comments, return all sorted comments
    if (showAllComments[topicId]) {
      return sortedComments;
    }
    
    // Otherwise, return only the last 3 comments (most recent)
    return sortedComments.slice(-3);
  }, [showAllComments]);

  const handleShowAllComments = useCallback((topicId) => {
    setShowAllComments(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  }, []);

  const handleShowReplyForm = useCallback((topicId) => {
    setShowReplyForm(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  }, []);

  const handleCancelReply = useCallback((topicId) => {
    setShowReplyForm(prev => ({
      ...prev,
      [topicId]: false
    }));
    setReplyText('');
  }, []);

  const handleSubmitReply = useCallback(async (topicId) => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequiredComment'));
      navigate('/login');
      return;
    }

    // Check if user has rated the course
    if (!userRating || userRating === 0) {
      toast.info(t('courses:courseDetail.pleaseRateFirst'));
      return;
    }

    try {
      if (!replyText.trim()) {
        toast.error(t('forumPage.validation.commentRequired'));
        return;
      }

      if (onAddComment) {
        await onAddComment(topicId, { content: replyText });
      }

      toast.success(t('forumPage.success.commentAdded'));
      setReplyText('');
      setShowReplyForm(prev => ({
        ...prev,
        [topicId]: false
      }));
    } catch (err) {
      console.error('Error adding reply:', err);
      toast.error(t('forumPage.errors.addCommentError'));
    }
  }, [isAuthenticated, user, navigate, t, replyText, onAddComment, userRating]);

  const handleTopicLike = useCallback(async () => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequired'));
      navigate('/login');
      return;
    }

    const wasLiked = isLiked;
    const newLikedState = !wasLiked;

    // Optimistic update
    setIsLiked(newLikedState);
    setIsDisliked(false); // Убираем дизлайк при лайке

    try {
      const response = await forumAPI.like('topics', topic._id);
      const updatedTopic = response?.data || {};

      // Обновляем состояние на основе ответа сервера
      const serverIsLiked = updatedTopic.likes?.includes(user._id) || false;
      const serverIsDisliked = updatedTopic.dislikes?.includes(user._id) || false;

      setIsLiked(serverIsLiked);
      setIsDisliked(serverIsDisliked);

      if (onLike) {
        onLike('topics', topic._id, updatedTopic);
      }

      if (serverIsLiked) {
        toast.success(t('forumPage.like.success') || 'Liked successfully!');
      } else {
        toast.success(t('forumPage.like.unlikeSuccess') || 'Like removed');
      }
    } catch (err) {
      // Revert optimistic update on error
      setIsLiked(wasLiked);
      toast.error(t('forumPage.errors.likeError'));
      console.error('Error liking topic:', err);
    }
  }, [isAuthenticated, user, navigate, t, onLike, topic._id, isLiked]);

  const handleNavigateToTopic = useCallback(() => {
    navigate(`/forum/${topic._id}`, { state: { from: 'forum' } });
  }, [topic._id, navigate]);

  // Simple like count from server
  const likeCount = Array.isArray(topic.likes) ? topic.likes.length : 0;
  const dislikeCount = Array.isArray(topic.dislikes) ? topic.dislikes.length : 0;
  
  const handleCommentLike = useCallback(async (commentId) => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequired'));
      navigate('/login');
      return;
    }

    const wasLiked = commentLikes[commentId] || false;
    const wasDisliked = commentDislikes[commentId] || false;
    const newLikedState = !wasLiked;

    // Optimistic update - точно как на бэкенде
    setCommentLikes(prev => ({
      ...prev,
      [commentId]: newLikedState,
    }));
    
    // Убираем дизлайк при лайке (как на бэкенде)
    setCommentDislikes(prev => ({
      ...prev,
      [commentId]: false,
    }));

    try {
      const response = await forumAPI.like('comments', commentId);
      const updatedComment = response?.data || {};

      // Обновляем состояние на основе ответа сервера
      const serverIsLiked = updatedComment.likes?.includes(user._id) || false;
      const serverIsDisliked = updatedComment.dislikes?.includes(user._id) || false;

      setCommentLikes(prev => ({
        ...prev,
        [commentId]: serverIsLiked,
      }));
      setCommentDislikes(prev => ({
        ...prev,
        [commentId]: serverIsDisliked,
      }));

      if (onLike) {
        onLike('comments', commentId, updatedComment);
      }

      // Показываем правильное сообщение
      if (serverIsLiked) {
        toast.success(t('forumPage.like.success') || 'Liked successfully!');
      } else {
        toast.success(t('forumPage.like.unlikeSuccess') || 'Like removed');
      }
    } catch (err) {
      // Revert optimistic update on error
      setCommentLikes(prev => ({
        ...prev,
        [commentId]: wasLiked,
      }));
      setCommentDislikes(prev => ({
        ...prev,
        [commentId]: wasDisliked,
      }));
      toast.error(t('forumPage.errors.likeError'));
      console.error('Error liking comment:', err);
    }
  }, [isAuthenticated, user, navigate, t, onLike, commentLikes, commentDislikes]);

  const handleCommentDislike = useCallback(async (commentId) => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequired'));
      navigate('/login');
      return;
    }

    const wasDisliked = commentDislikes[commentId] || false;
    const wasLiked = commentLikes[commentId] || false;
    const newDislikedState = !wasDisliked;

    // Optimistic update - точно как на бэкенде
    setCommentDislikes(prev => ({
      ...prev,
      [commentId]: newDislikedState,
    }));
    
    // Убираем лайк при дизлайке (как на бэкенде)
    setCommentLikes(prev => ({
      ...prev,
      [commentId]: false,
    }));

    try {
      const response = await forumAPI.dislike('comments', commentId);
      const updatedComment = response?.data || {};

      // Обновляем состояние на основе ответа сервера
      const serverIsLiked = updatedComment.likes?.includes(user._id) || false;
      const serverIsDisliked = updatedComment.dislikes?.includes(user._id) || false;

      setCommentLikes(prev => ({
        ...prev,
        [commentId]: serverIsLiked,
      }));
      setCommentDislikes(prev => ({
        ...prev,
        [commentId]: serverIsDisliked,
      }));

      if (onLike) {
        onLike('comments', commentId, updatedComment);
      }

      // Показываем правильное сообщение
      if (serverIsDisliked) {
        toast.success(t('forumPage.dislike.success') || 'Comment disliked');
      } else {
        toast.success(t('forumPage.dislike.removed') || 'Dislike removed');
      }
    } catch (err) {
      // Revert optimistic update on error
      setCommentDislikes(prev => ({
        ...prev,
        [commentId]: wasDisliked,
      }));
      setCommentLikes(prev => ({
        ...prev,
        [commentId]: wasLiked,
      }));
      toast.error(t('forumPage.errors.dislikeError') || 'Error disliking');
      console.error('Error disliking comment:', err);
    }
  }, [isAuthenticated, user, navigate, t, onLike, commentDislikes, commentLikes]);

  const handleTopicDislike = useCallback(async () => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequired'));
      navigate('/login');
      return;
    }

    const wasDisliked = isDisliked;
    const newDislikedState = !wasDisliked;

    // Optimistic update
    setIsDisliked(newDislikedState);
    setIsLiked(false); // Убираем лайк при дизлайке

    try {
      const response = await forumAPI.dislike('topics', topic._id);
      const updatedTopic = response?.data || {};

      // Обновляем состояние на основе ответа сервера
      const serverIsLiked = updatedTopic.likes?.includes(user._id) || false;
      const serverIsDisliked = updatedTopic.dislikes?.includes(user._id) || false;

      setIsLiked(serverIsLiked);
      setIsDisliked(serverIsDisliked);

      if (onLike) {
        onLike('topics', topic._id, updatedTopic);
      }

      // Показываем правильное сообщение
      if (serverIsDisliked) {
        toast.success(t('forumPage.dislike.success') || 'Topic disliked');
      } else {
        toast.success(t('forumPage.dislike.removed') || 'Dislike removed');
      }
    } catch (err) {
      // Revert optimistic update on error
      setIsDisliked(wasDisliked);
      toast.error(t('forumPage.errors.dislikeError') || 'Error disliking');
      console.error('Error toggling dislike:', err);
    }
  }, [isAuthenticated, user, navigate, t, onLike, topic._id, isDisliked]);

  const authorName = topic.author?.userMeta?.fullName || topic.author?.username || t('forumPage.userActivity.anonymous');
  
  if (!topic) {
    return null;
  }
  
  return (
    <>
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Avatar */}
          <Avatar
            src={getUserAvatarSrc(topic.author || {})}
            alt={authorName}
            sx={{
              width: 40,
              height: 40,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
            onClick={() => topic.author?._id && navigate(`/profile/${topic.author._id}`)}
          >
            {authorName.charAt(0).toUpperCase()}
          </Avatar>

          {/* Content */}
          <Box sx={{ flexGrow: 1 }}>
            {/* Header with name, rating, and time */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box
                sx={{
                  cursor: 'pointer',
                  borderRadius: '6px',
                  p: 0.5,
                  mx: -0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => topic.author?._id && navigate(`/profile/${topic.author._id}`)}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {authorName}
                </Typography>
                {/* Rating display for reviews */}
                {topic.author?.courseRating && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Rating
                      value={topic.author?.courseRating}
                      readOnly
                      precision={0.5}
                      size="small"
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: '#fbbf24', // yellow-400
                        },
                      }}
                    />
                    <Typography variant="caption" fontWeight="bold" color="text.secondary">
                      {topic.author?.courseRating?.toFixed(1)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDate(topic.createdAt, 'relative')}
              </Typography>
            </Box>

            {/* Topic title */}
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 1, cursor: 'pointer' }}
              onClick={handleNavigateToTopic}
            >
              {topic.title}
            </Typography>

            {/* Topic content */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.5 }}
            >
              {topic.content}
            </Typography>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="text"
                size="small"
                startIcon={<ThumbUpIcon />}
                onClick={handleTopicLike}
                disabled={!isAuthenticated}
                sx={{
                  color: isLiked ? theme.palette.primary.main : 'text.secondary',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light + '20',
                  },
                  '&.Mui-disabled': {
                    color: 'text.disabled',
                  },
                }}
              >
                {likeCount}
              </Button>
              
              <Button
                variant="text"
                size="small"
                startIcon={<ThumbDownIcon />}
                onClick={handleTopicDislike}
                disabled={!isAuthenticated}
                sx={{
                  color: isDisliked ? theme.palette.error.main : 'text.secondary',
                  '&:hover': {
                    color: theme.palette.error.main,
                    backgroundColor: theme.palette.error.light + '20',
                  },
                  '&.Mui-disabled': {
                    color: 'text.disabled',
                  },
                }}
              >
                {dislikeCount}
              </Button>

              <Button
                variant="text"
                size="small"
                startIcon={<MessageSquareIcon />}
                onClick={() => handleShowReplyForm(topic._id)}
                disabled={!isAuthenticated || !userRating || userRating === 0}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-disabled': {
                    color: 'text.disabled',
                  },
                }}
              >
                Reply
              </Button>

              {/* Show More Comments button */}
              {topic.comments && topic.comments.length > 3 && !showAllComments[topic._id] && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ChatBubbleOutlineIcon />}
                  onClick={() => handleShowAllComments(topic._id)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {t('forumPage.comments.showMore', { count: topic.comments.length - 3 })}
                </Button>
              )}

              {/* Show Less button when showing all comments */}
              {topic.comments && topic.comments.length > 3 && showAllComments[topic._id] && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ChatBubbleOutlineIcon />}
                  onClick={() => handleShowAllComments(topic._id)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {t('forumPage.comments.hideOlder')}
                </Button>
              )}
            </Box>

            {/* Reply form */}
            {showReplyForm[topic._id] && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write your reply..."
                  variant="outlined"
                  size="small"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => handleCancelReply(topic._id)}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleSubmitReply(topic._id)}
                    disabled={!replyText.trim()}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    Post Reply
                  </Button>
                </Box>
              </Box>
            )}

            {/* Comments section */}
            {topic.comments && topic.comments.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {getDisplayComments(topic.comments, topic._id).map((comment) => {
                  const commentAuthorName = comment.author?.userMeta?.fullName || comment.author?.username || t('forumPage.userActivity.anonymous');
                  
                  return (
                    <Box
                      key={comment._id}
                      sx={{
                        p: 2,
                        borderRadius: '8px',
                        backgroundColor: 'action.hover',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        src={getUserAvatarSrc(comment.author)}
                        alt={commentAuthorName}
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() => comment.author?._id && navigate(`/profile/${comment.author._id}`)}
                      >
                        {commentAuthorName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                            sx={{
                              cursor: 'pointer',
                              borderRadius: '4px',
                              p: 0.5,
                              mx: -0.5,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                            onClick={() => comment.author?._id && navigate(`/profile/${comment.author._id}`)}
                          >
                            {commentAuthorName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(comment.createdAt, 'relative')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {comment.content}
                        </Typography>
                        
                        {/* Comment like/dislike buttons */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<ThumbUpIcon />}
                            onClick={() => handleCommentLike(comment._id)}
                            disabled={!isAuthenticated}
                            sx={{
                              color: commentLikes[comment._id] ? theme.palette.primary.main : 'text.secondary',
                              '&:hover': {
                                color: theme.palette.primary.main,
                                backgroundColor: theme.palette.primary.light + '20',
                              },
                              '&.Mui-disabled': {
                                color: 'text.disabled',
                              },
                              minWidth: 'auto',
                              px: 1,
                            }}
                          >
                            {Array.isArray(comment.likes) ? comment.likes.length : 0}
                          </Button>
                          
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<ThumbDownIcon />}
                            onClick={() => handleCommentDislike(comment._id)}
                            disabled={!isAuthenticated}
                            sx={{
                              color: commentDislikes[comment._id] ? theme.palette.error.main : 'text.secondary',
                              '&:hover': {
                                color: theme.palette.error.main,
                                backgroundColor: theme.palette.error.light + '20',
                              },
                              '&.Mui-disabled': {
                                color: 'text.disabled',
                              },
                              minWidth: 'auto',
                              px: 1,
                            }}
                          >
                            {Array.isArray(comment.dislikes) ? comment.dislikes.length : 0}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

    </>
  );
};

export default TopicCard;
